import React from 'react';

import { SvgIcon } from '@progress/kendo-react-common';
import { checkIcon } from '@progress/kendo-svg-icons';
import './index.css';

type Status = 'completed' | 'inProgress' | 'pending';

interface StatusItem {
  name: string;
  status: Status;
}
interface StatusTrackerProps {
  stage: string;
  ntn: string;
}

const getStatusClass = (status: Status): string => {
  if (status === 'completed' || status === 'inProgress') {
    return 'tw-bg-unm-interactive01-400 tw-border-none';
  } else {
    return 'tw-border-unm-secondary01-700';
  }
};

const getConnectorLine = (
  currentStatus: Status,
  nextStatus: Status,
  isleftLine: boolean,
): JSX.Element | null => {
  const isActive = isleftLine
    ? (currentStatus === 'completed' || currentStatus === 'inProgress') &&
      (nextStatus === 'completed' ||
        nextStatus === 'inProgress' ||
        nextStatus === 'pending' ||
        nextStatus === null)
    : (currentStatus === 'completed' || currentStatus === 'inProgress') &&
      (nextStatus === 'completed' || nextStatus === 'inProgress');

  const childClassName = [
    'tw-h-0.5',
    'tw-w-6',
    'min-[440px]:tw-w-9',
    'min-[540px]:tw-w-12',
    'md:tw-w-16',
    'lg:tw-w-20',
    isActive ? 'tw-bg-unm-interactive01-400' : 'tw-bg-unm-secondary01-300',
  ].join(' ');

  const parentClassName = [
    'tw-flex',
    'tw-items-center',
    'tw-h-5',
    'tw-z-0',
  ].join(' ');

  return (
    <div className={parentClassName}>
      <div className={childClassName} />
    </div>
  );
};

const checkForIcon = (status: Status) => {
  if (status === 'completed' || status === 'inProgress') {
    return (
      <SvgIcon icon={checkIcon} className="tw-text-white status-check-icon" />
    );
  } else {
    return;
  }
};

const getTextClass = (status: Status): string => {
  if (status === 'inProgress') {
    return 'tw-text-unm-adhoc2 tw-font-sans tw-font-semibold tw-text-[16px] tw-underline tw-decoration-unm-interactive01-400 tw-decoration-1 sm:tw-decoration-2 tw-underline-offset-4';
  } else {
    return 'tw-text-unm-adhoc2 tw-font-sans tw-font-semibold tw-text-[16px]';
  }
};

const recieved = '1 Received';
const Review = '2 Review';
const Active = '3 Active';
const closed = '4 Closed';

const setStatusItems = (stage: string): StatusItem[] => {
  if (stage === 'Closed') {
    return [
      { name: recieved, status: 'completed' as Status },
      { name: Review, status: 'completed' as Status },
      { name: Active, status: 'completed' as Status },
      { name: closed, status: 'completed' as Status },
    ];
  } else if (stage === 'Active') {
    return [
      { name: recieved, status: 'completed' as Status },
      { name: Review, status: 'completed' as Status },
      { name: Active, status: 'inProgress' as Status },
      { name: closed, status: 'pending' as Status },
    ];
  } else if (stage === 'Review') {
    return [
      { name: recieved, status: 'completed' as Status },
      { name: Review, status: 'inProgress' as Status },
      { name: Active, status: 'pending' as Status },
      { name: closed, status: 'pending' as Status },
    ];
  } else if (stage === 'Received') {
    return [
      { name: recieved, status: 'inProgress' as Status },
      { name: Review, status: 'pending' as Status },
      { name: Active, status: 'pending' as Status },
      { name: closed, status: 'pending' as Status },
    ];
  } else {
    return [
      { name: recieved, status: 'completed' as Status },
      { name: Review, status: 'pending' as Status },
      { name: Active, status: 'pending' as Status },
      { name: closed, status: 'inProgress' as Status },
    ];
  }
};
export const StatusTrackerHorizontal = ({ stage, ntn }: StatusTrackerProps) => {
  const statusItems: StatusItem[] = setStatusItems(stage);

  return (
    <div id={`status-tracker-${ntn}`} className="tw-w-full">
      <div
        className="tw-flex tw-flex-col   tw-px-2 tw-py-4 
                tw-bg-white"
      >
        {/* Title */}
        <p className="tw-text-unm-interactive01-400 tw-font-semibold tw-text-base tw-mb-3">
          Claim Status
        </p>
        {/* Steps */}
        <div className="tw-flex tw-flex-row tw-justify-center">
          {statusItems.map((item, index) => (
            <React.Fragment key={item.name}>
              <div className="tw-flex tw-flex-col tw-items-center tw-relative tw-z-10">
                {/* Icon and line container */}
                <div className="tw-flex tw-items-center">
                  {/* Connector Line - Left*/}
                  {item.name === '1 Received' ? (
                    <div className="tw-w-6 min-[440px]:tw-w-8 sm:tw-w-10 md:tw-w-16 lg:tw-w-20" />
                  ) : (
                    getConnectorLine(
                      item.status,
                      statusItems[index + 1]?.status ?? null,
                      true,
                    )
                  )}
                  {/* Icon */}
                  <div
                    className={`tw-relative tw-flex tw-items-center tw-justify-center 
                        tw-font-semibold tw-text-xs
                        tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-rounded-full tw-border tw-border-solid
                        ${getStatusClass(item.status)}`}
                  >
                    {checkForIcon(item.status)}
                  </div>
                  {/* Connector Line - Right*/}
                  {item.name === '4 Closed' && (
                    <div className="tw-w-6 min-[440px]:tw-w-8 sm:tw-w-10 md:tw-w-16 lg:tw-w-20" />
                  )}
                  {index < statusItems.length - 1 &&
                    getConnectorLine(
                      item.status,
                      statusItems[index + 1]?.status ?? null,
                      false,
                    )}
                </div>

                {/* Label */}
                <span
                  className={`tw-mt-2 tw-text-sm ${getTextClass(item.status)}`}
                >
                  {item.name.split(' ').slice(1).join(' ')}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export const StatusTrackerVertical = ({ stage, ntn }: StatusTrackerProps) => {
  const statusItems: StatusItem[] = setStatusItems(stage);

  return (
    <div id={`status-tracker-${ntn}`} className="tw-w-full">
      <div className="tw-px-4 tw-py-3 tw-bg-white">
        {/* Title */}
        <p className="tw-text-unm-interactive01-400 tw-font-semibold tw-text-base tw-mb-3">
          Claim Status
        </p>
        {/* Steps */}
        <div className="tw-flex tw-flex-col">
          {statusItems.map((item, index) => (
            <React.Fragment key={item.name}>
              {/* Step row */}
              <div className="tw-flex tw-items-center tw-gap-3">
                {/* Circle icon */}
                <div
                  className={`tw-flex tw-items-center tw-justify-center tw-shrink-0
                    tw-font-semibold tw-text-sm
                    tw-w-5 tw-h-5 tw-rounded-full tw-border tw-border-solid
                    ${getStatusClass(item.status)}`}
                >
                  {checkForIcon(item.status)}
                </div>
                {/* Label */}
                <span className={`tw-text-md ${getTextClass(item.status)}`}>
                  {item.name.split(' ').slice(1).join(' ')}
                </span>
              </div>
              {/* Vertical connector line */}
              {index < statusItems.length - 1 && (
                <div className="tw-pl-[10px]">
                  <div
                    className={`tw-w-0.5 tw-h-3 ${
                      item.status === 'completed'
                        ? 'tw-bg-unm-interactive01-400'
                        : 'tw-bg-unm-secondary01-300'
                    }`}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
